<script setup lang="ts">
/**
 * Slideover form for creating and editing item types.
 * Includes display name, description, icon picker, color picker,
 * sort order, detection patterns, and active toggle.
 */
import { router } from "@inertiajs/vue3"
import { computed, ref, watch } from "vue"

import ColorSwatchPicker from "@/components/ColorSwatchPicker.vue"
import IconPicker from "@/components/IconPicker.vue"
import SlideoverFooter from "@/components/SlideoverFooter.vue"
import { itemTypePath, itemTypesPath } from "@/routes"
import type { ItemTypeSummary } from "@/types/models"

const props = defineProps<{
  open: boolean
  colors: string[]
  icons: string[]
  itemType: ItemTypeSummary | null
}>()

const emit = defineEmits<{
  "update:open": [value: boolean]
}>()

const displayName = ref("")
const description = ref("")
const iconClass = ref("box")
const colorClass = ref("gray")
const sortOrder = ref(0)
const detectionPatternsText = ref("")
const active = ref(true)
const saving = ref(false)
const formErrors = ref<Record<string, string>>({})

const isEdit = computed(() => !!props.itemType)
const title = computed(() => (isEdit.value ? "Edit Item Type" : "New Item Type"))
const submitLabel = computed(() => (isEdit.value ? "Save" : "Create"))

watch(
  () => props.open,
  (open) => {
    if (open) {
      displayName.value = props.itemType?.display_name ?? ""
      description.value = props.itemType?.description ?? ""
      iconClass.value = props.itemType?.icon_class ?? "box"
      colorClass.value = props.itemType?.color_class ?? "gray"
      sortOrder.value = props.itemType?.sort_order ?? 0
      detectionPatternsText.value = (props.itemType?.auto_detection_patterns ?? []).join(", ")
      active.value = props.itemType?.active ?? true
      formErrors.value = {}
    }
  },
)

function close() {
  emit("update:open", false)
}

function submit() {
  saving.value = true
  const data = {
    item_type: {
      display_name: displayName.value,
      description: description.value || null,
      icon_class: iconClass.value,
      color_class: colorClass.value,
      sort_order: sortOrder.value,
      detection_patterns_text: detectionPatternsText.value,
      active: active.value,
    },
  }

  if (isEdit.value) {
    router.patch(itemTypePath({ id: props.itemType!.id }), data, {
      onSuccess: () => close(),
      onError: (errors) => (formErrors.value = errors),
      onFinish: () => (saving.value = false),
    })
  } else {
    router.post(itemTypesPath(), data, {
      onError: (errors) => (formErrors.value = errors),
      onFinish: () => (saving.value = false),
    })
  }
}
</script>

<template>
  <USlideover :open="open" :title="title" @update:open="emit('update:open', $event)">
    <template #body>
      <form id="item-type-form" class="flex flex-col gap-6" @submit.prevent="submit">
        <UFormField label="Name" required :error="formErrors.display_name" class="w-full">
          <UInput
            v-model="displayName"
            placeholder="e.g. Electronics"
            maxlength="50"
            autofocus
            class="w-full"
          />
        </UFormField>

        <UFormField label="Description" class="w-full">
          <UTextarea
            v-model="description"
            placeholder="Optional description"
            :rows="2"
            class="w-full"
          />
        </UFormField>

        <UFormField label="Icon">
          <IconPicker v-model="iconClass" :icons="icons" :color="colorClass" />
        </UFormField>

        <UFormField label="Color">
          <ColorSwatchPicker v-model="colorClass" :colors="colors" />
        </UFormField>

        <UFormField label="Sort Order" description="Lower numbers appear first." class="w-full">
          <UInputNumber v-model="sortOrder" :min="0" class="w-full" />
        </UFormField>

        <UFormField
          label="Detection Patterns"
          description="Comma-separated words that auto-assign this type to new items."
          class="w-full"
        >
          <UTextarea
            v-model="detectionPatternsText"
            placeholder="guitar, piano, drums, violin"
            :rows="2"
            class="w-full"
          />
        </UFormField>

        <UFormField>
          <div class="flex items-center gap-2">
            <USwitch v-model="active" />
            <span class="text-sm">{{
              active ? "Active — visible in pickers" : "Inactive — hidden from pickers"
            }}</span>
          </div>
        </UFormField>
      </form>
    </template>

    <template #footer>
      <SlideoverFooter
        form-id="item-type-form"
        :submit-label="submitLabel"
        :loading="saving"
        @cancel="close"
      />
    </template>
  </USlideover>
</template>
